import { useState } from "react";
import {
  Send,
  Check,
  X,
  AlertTriangle,
  Clock,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { useStudyBuilderStore, selectIsReadOnly } from "@/stores/study-builder-store";
import {
  useBuildReviews,
  useSubmitForReview,
  useReviewArtifact,
  useReviewComments,
} from "@/hooks/use-protocol-builder";
import type { ArtifactReview, ReviewerEntry } from "@/api/protocol-types";

interface ArtifactReviewBarProps {
  artifactType: string;
  artifactId: string;
}

export function ArtifactReviewBar({
  artifactType,
  artifactId,
}: ArtifactReviewBarProps) {
  const buildId = useStudyBuilderStore((s) => s.buildId);
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);
  const uid = useAuthStore((s) => s.uid);
  const role = useAuthStore((s) => s.role);
  const { data: reviewsData } = useBuildReviews(buildId ?? undefined);
  const { data: commentsData } = useReviewComments(
    buildId ?? undefined,
    artifactType,
    artifactId,
  );
  const submitForReview = useSubmitForReview();
  const reviewArtifact = useReviewArtifact();

  const [decisionComment, setDecisionComment] = useState("");
  const [showDecisionInput, setShowDecisionInput] = useState<
    "approve" | "changes_requested" | "rejected" | null
  >(null);

  if (!buildId) return null;

  // Find active review for this artifact
  const review = reviewsData?.reviews?.find(
    (r: ArtifactReview) =>
      r.artifactType === artifactType &&
      r.artifactId === artifactId &&
      r.status !== "approved" &&
      r.status !== "rejected",
  );

  // Check if there's an approved review
  const approvedReview = reviewsData?.reviews?.find(
    (r: ArtifactReview) =>
      r.artifactType === artifactType &&
      r.artifactId === artifactId &&
      r.status === "approved",
  );

  const currentReview = review || approvedReview;

  // Is the current user an assigned reviewer with a pending decision?
  const myReviewerEntry = currentReview?.reviewers?.find(
    (r: ReviewerEntry) => r.uid === uid && r.decision === "pending",
  );

  const commentCount = commentsData?.comments?.length ?? 0;
  const unresolvedCount =
    commentsData?.comments?.filter((c) => !c.resolved).length ?? 0;

  const handleSubmitForReview = () => {
    submitForReview.mutate({ buildId, artifactType, artifactId });
  };

  const handleDecision = (decision: "approved" | "changes_requested" | "rejected") => {
    if (!currentReview) return;
    reviewArtifact.mutate(
      {
        buildId,
        reviewId: currentReview.id,
        decision,
        comment: decisionComment || undefined,
      },
      {
        onSuccess: () => {
          setShowDecisionInput(null);
          setDecisionComment("");
        },
      },
    );
  };

  const status = currentReview?.status ?? "draft";

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
      <div className="flex items-center gap-2">
        {/* Status badge */}
        <ReviewStatusBadge status={status} />

        {/* Reviewer avatars */}
        {currentReview?.reviewers && currentReview.reviewers.length > 0 && (
          <div className="flex items-center -space-x-1.5">
            {currentReview.reviewers.map((reviewer: ReviewerEntry) => (
              <ReviewerAvatar key={reviewer.uid} reviewer={reviewer} />
            ))}
          </div>
        )}

        <div className="flex-1" />

        {/* Comment count */}
        {commentCount > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <MessageSquare className="h-3 w-3" />
            {unresolvedCount > 0 ? `${unresolvedCount} open` : `${commentCount}`}
          </span>
        )}

        {/* Action buttons */}
        {!isReadOnly && !currentReview && role && role !== "viewer" && (
          <button
            onClick={handleSubmitForReview}
            disabled={submitForReview.isPending}
            className="flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitForReview.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            Submit for Review
          </button>
        )}

        {!isReadOnly && myReviewerEntry && !showDecisionInput && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowDecisionInput("approve")}
              className="flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700"
            >
              <Check className="h-3 w-3" />
              Approve
            </button>
            <button
              onClick={() => setShowDecisionInput("changes_requested")}
              className="flex items-center gap-1 rounded-md border border-amber-500 px-2 py-1 text-[11px] font-medium text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
            >
              <AlertTriangle className="h-3 w-3" />
              Changes
            </button>
            <button
              onClick={() => setShowDecisionInput("rejected")}
              className="flex items-center gap-1 rounded-md border border-red-500 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
            >
              <X className="h-3 w-3" />
              Reject
            </button>
          </div>
        )}
      </div>

      {/* Decision comment input */}
      {showDecisionInput && (
        <div className="flex items-center gap-2">
          <input
            value={decisionComment}
            onChange={(e) => setDecisionComment(e.target.value)}
            placeholder="Optional comment..."
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground"
          />
          <button
            onClick={() => handleDecision(showDecisionInput === "approve" ? "approved" : showDecisionInput)}
            disabled={reviewArtifact.isPending}
            className={cn(
              "rounded-md px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50",
              showDecisionInput === "approve"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : showDecisionInput === "changes_requested"
                  ? "bg-amber-600 hover:bg-amber-700"
                  : "bg-red-600 hover:bg-red-700",
            )}
          >
            {reviewArtifact.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              "Confirm"
            )}
          </button>
          <button
            onClick={() => {
              setShowDecisionInput(null);
              setDecisionComment("");
            }}
            className="text-[11px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    draft: {
      label: "Draft",
      className: "border-border bg-muted text-muted-foreground",
    },
    pending: {
      label: "Pending",
      className: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    },
    in_review: {
      label: "In Review",
      className: "border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400",
    },
    changes_requested: {
      label: "Changes Requested",
      className: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    },
    approved: {
      label: "Approved",
      className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    },
    rejected: {
      label: "Rejected",
      className: "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400",
    },
  };

  const c = config[status] ?? config.draft;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
        c.className,
      )}
    >
      {c.label}
    </span>
  );
}

function ReviewerAvatar({ reviewer }: { reviewer: ReviewerEntry }) {
  const initials = reviewer.displayName
    ? reviewer.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  const decisionIcon =
    reviewer.decision === "approved" ? (
      <Check className="h-2 w-2 text-emerald-600" />
    ) : reviewer.decision === "changes_requested" ? (
      <AlertTriangle className="h-2 w-2 text-amber-600" />
    ) : reviewer.decision === "rejected" ? (
      <X className="h-2 w-2 text-red-600" />
    ) : (
      <Clock className="h-2 w-2 text-muted-foreground" />
    );

  const borderColor =
    reviewer.decision === "approved"
      ? "ring-emerald-500"
      : reviewer.decision === "changes_requested"
        ? "ring-amber-500"
        : reviewer.decision === "rejected"
          ? "ring-red-500"
          : "ring-border";

  return (
    <div
      className={cn(
        "relative flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[8px] font-bold ring-2 ring-offset-1 ring-offset-background",
        borderColor,
      )}
      title={`${reviewer.displayName} (${reviewer.role}) - ${reviewer.decision}`}
    >
      {initials}
      <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-background">
        {decisionIcon}
      </span>
    </div>
  );
}
